import {
  collection,
  getDocs,
  doc,
  writeBatch,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { buildDesignIndexDocId, normalizeDesignName } from './designNameMatching';
import { getMockupUrl } from './fileHelpers';
import { CUSTOMER_DESIGN_MOCKUPS_COLLECTION } from './designMockupIndex';
import { buildLineSnapshotFromLine, invalidateCustomerDesignLibrary } from './customerDesignLibrary';

function toMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  return 0;
}

/**
 * Scan orderLines + orders and pick the newest mockup per customer + design name.
 */
export async function buildDesignMockupIndexFromHistory({ onProgress } = {}) {
  const report = (message) => {
    if (onProgress) onProgress(message);
  };

  report('Loading orders...');
  const ordersSnap = await getDocs(collection(db, 'orders'));
  const ordersById = new Map();

  ordersSnap.docs.forEach((orderDoc) => {
    const data = orderDoc.data();
    ordersById.set(orderDoc.id, {
      customerId: data.customerId || null,
      poNumber: data.poNumber || data.orderNumber || null
    });
  });

  report(`Loaded ${ordersById.size} orders. Scanning order lines...`);
  const linesSnap = await getDocs(collection(db, 'orderLines'));

  const winners = new Map();
  let scanned = 0;
  let withMockup = 0;
  let skippedNoCustomer = 0;
  let skippedNoDesignName = 0;

  linesSnap.docs.forEach((lineDoc) => {
    scanned += 1;
    const line = lineDoc.data();
    const mockupUrl = getMockupUrl(line);
    if (!mockupUrl) return;

    withMockup += 1;

    const orderMeta = ordersById.get(line.orderId) || {};
    const customerId = orderMeta.customerId;
    const designName = line.designName;

    if (!customerId) {
      skippedNoCustomer += 1;
      return;
    }
    if (!designName?.trim()) {
      skippedNoDesignName += 1;
      return;
    }

    const docId = buildDesignIndexDocId(customerId, designName);
    if (!docId) return;

    const updatedAtMs = Math.max(
      toMillis(line.updatedAt),
      toMillis(line.createdAt)
    );

    const candidate = {
      docId,
      customerId,
      normalizedKey: normalizeDesignName(designName).replace(/\s+/g, '_'),
      displayName: designName.trim(),
      mockupUrl,
      mockupFileName: line.files?.mockup?.fileName || null,
      sourceLineId: lineDoc.id,
      sourceOrderId: line.orderId || null,
      sourceOrderNumber: orderMeta.poNumber || null,
      lineSnapshot: buildLineSnapshotFromLine(line),
      updatedAtMs
    };

    const existing = winners.get(docId);
    if (!existing || candidate.updatedAtMs >= existing.updatedAtMs) {
      winners.set(docId, candidate);
    }
  });

  const entries = Array.from(winners.values());

  report(
    `Scan complete — ${scanned} lines, ${withMockup} with mockups, ${entries.length} index entries` +
      (skippedNoCustomer ? ` (${skippedNoCustomer} skipped: no customer on order)` : '')
  );

  return {
    scanned,
    withMockup,
    skippedNoCustomer,
    skippedNoDesignName,
    indexCount: entries.length,
    preview: entries.slice(0, 5),
    entries
  };
}

/**
 * Backfill customerDesignMockups using the logged-in app's Firebase client.
 * No service account required.
 */
export async function runBackfillDesignMockupIndex({ dryRun = false, onProgress } = {}) {
  const result = await buildDesignMockupIndexFromHistory({ onProgress });

  if (dryRun) {
    if (onProgress) onProgress('Preview finished — no database changes made.');
    return { ...result, written: 0, dryRun: true };
  }

  const { entries } = result;
  const batchSize = 400;
  let written = 0;

  for (let i = 0; i < entries.length; i += batchSize) {
    const batch = writeBatch(db);
    const chunk = entries.slice(i, i + batchSize);

    chunk.forEach((entry) => {
      const ref = doc(db, CUSTOMER_DESIGN_MOCKUPS_COLLECTION, entry.docId);
      batch.set(
        ref,
        {
          customerId: entry.customerId,
          normalizedKey: entry.normalizedKey,
          displayName: entry.displayName,
          mockupUrl: entry.mockupUrl,
          mockupFileName: entry.mockupFileName,
          sourceLineId: entry.sourceLineId,
          sourceOrderId: entry.sourceOrderId,
          sourceOrderNumber: entry.sourceOrderNumber,
          ...(entry.lineSnapshot ? { lineSnapshot: entry.lineSnapshot } : {}),
          updatedAt: Timestamp.fromMillis(entry.updatedAtMs || Date.now())
        },
        { merge: true }
      );
    });

    await batch.commit();
    written += chunk.length;
    if (onProgress) {
      onProgress(`Wrote ${written} / ${entries.length} index documents...`);
    }
  }

  if (onProgress) onProgress(`Backfill finished — wrote ${written} index documents.`);
  invalidateCustomerDesignLibrary();

  return { ...result, written, dryRun: false };
}
